import type {TFunction} from 'i18next'
import {useDispatch as reduxUseDispatch} from 'react-redux'
import type {Action, Dispatch} from 'redux'
import type {ThunkAction, ThunkDispatch} from 'redux-thunk'

import {feedbackPost, projectLaborStatsPost} from './api'

const ASYNC_MARKER = 'ASYNC_MARKER'

// Set of actions we want to log in the analytics
export const actionTypesToLog = {
  ADVICE_PAGE_IS_SHOWN: 'Advice page shown',
  DISPLAY_TOAST_MESSAGE: 'Display toast message',
  PAGE_IS_LOADED: 'Page is loaded',
  REGISTER_USER: 'Register new user',
}


// Get the list of paths of defined fields.
// Only exported for testing.
export function getDefinedFieldsPath<T>(proto: T, prefix = ''): readonly string[] {
  if (typeof proto !== 'object' || !proto || Array.isArray(proto)) {
    return []
  }
  return Object.entries(proto).flatMap(([key, value]): readonly string[] => {
    if (typeof value === 'undefined') {
      return []
    }
    const subPaths = getDefinedFieldsPath(value, prefix + key + '.')
    if (subPaths.length) {
      return subPaths
    }
    return [prefix + key]
  })
}


// Actions and action generators follow the following conventions:
// - action themselves are static objects, their names end by `Action`.
// - action generators:
//     - must take at least one parameter
//     - may not have a name ending by `Action'.
//     - always return a Promise or a thunk even when not asynchronous.
// Thanks to this conventions, callers of such actions may always rely on
// actions being plain actions, and action generated to always being resolved
// to a promise when dispatched even if the action is synchronous.
// `await dispatch(doSomething(aboutThat))`

// Plain actions, keep them grouped and alpha sorted.

export type HideToasterMessageAction = Readonly<Action<'HIDE_TOASTER_MESSAGE'>>
const hideToasterMessageAction: HideToasterMessageAction = {type: 'HIDE_TOASTER_MESSAGE'}

export interface ProjectAction<T extends string> extends Readonly<Action<T>> {
  readonly project: bayes.bob.Project
}

export interface VisualElementAction<T extends string> extends Readonly<Action<T>> {
  readonly visualElement: string
}

export interface DisplayToastMessageAction extends Readonly<Action<'DISPLAY_TOAST_MESSAGE'>> {
  readonly error: string
}

function displayToasterMessage(error: string): DisplayToastMessageAction {
  return {error, type: 'DISPLAY_TOAST_MESSAGE'}
}

// Asynchronous action generators.

interface AsyncError {
  readonly error: Error | string
  readonly status: 'error'
}


export type AsyncAction<T extends string, Result> = Readonly<Action<T>> & {
  ASYNC_MARKER: 'ASYNC_MARKER'
  fetchingKey?: string
  ignoreFailure?: boolean
} & ({readonly status?: ''} | AsyncError | {
  readonly response: Result
  readonly status: 'success'
})


// Wrap an async function by dispatching an action before and after the
// function: the initial action has the given type and an ASYNC_MARKER, the
// final action has the same type and marker but also a status 'success' or
// 'error' with additional response or error var. The asyncFunc doesn't take
// any parameter and should return a promise.
// The promise returned by this function always resolve, to undefined if
// there's an error.
// TODO(cyrille): Do not call again when the fetchingKey is already fetching.
export function wrapAsyncAction<T extends string, Extra, Result>(
  actionType: T, asyncFunc: () => Promise<Result>, options?: Extra, fetchingKey?: string):
  ThunkAction<Promise<Result|void>, unknown, unknown, Action> {
  return async (dispatch): Promise<Result|void> => {
    const action = {...options, ASYNC_MARKER, fetchingKey, type: actionType}
    dispatch(action)
    try {
      const response = await asyncFunc()
      dispatch({...action, response, status: 'success'})
      return response
    } catch (error) {
      dispatch({...action, error: error as Error, status: 'error'})
    }
  }
}

// Asynchronous actions wrapped with the dispatched actions (see wrapAsyncAction).

export interface WithFeedback {
  feedback: bayes.bob.Feedback
}

export interface WithProjectFeedback {
  feedback: bayes.bob.ProjectFeedback
}

export interface StateForFeedback {
  app: AppState
  user: bayes.bob.User
}

type DispatchFeedbackActions<T extends string> =
  & ThunkDispatch<unknown, unknown, AsyncAction<T, string> & WithFeedback>
  & Dispatch<AllActions>

export function sendFeedback<T extends string, A extends AsyncAction<T, string> & WithFeedback>(
  type: T, source: bayes.bob.FeedbackSource, feedback: bayes.bob.Feedback, t: TFunction,
  extraFields?: Omit<bayes.bob.Feedback, 'source' | 'feedback' | 'userId'>):
  ThunkAction<Promise<unknown|void>, StateForFeedback, unknown, A> {
  return async (dispatch: DispatchFeedbackActions<T>): Promise<unknown|void> => {
    const response = await dispatch(wrapAsyncAction<T, WithFeedback, unknown>(
      type,
      (): Promise<unknown|void> => feedbackPost({
        ...feedback,
        source,
        ...extraFields,
      }),
      {feedback},
    ))
    if (response) {
      dispatch(displayToasterMessage(t('Merci pour ce retour')))
    }
    return response
  }
}

export type PageIsLoadedAction = Readonly<Action<'PAGE_IS_LOADED'>> & {
  readonly location: {
    readonly pathname: string
  }
  readonly timeToFirstInteractiveMillisecs?: number
}

function pageIsLoaded(location: {readonly pathname: string}):
ThunkAction<PageIsLoadedAction, RootState, unknown, PageIsLoadedAction> {
  return (dispatch, getState): PageIsLoadedAction => {
    const {app: {hasLoadedApp}} = getState()
    return dispatch({
      location: location || window.location,
      // TODO (cyrille): Maybe sample this measurement if we don't want to slow down everyone.
      ...!hasLoadedApp && window.performance && window.performance.now && {
        timeToFirstInteractiveMillisecs: window.performance.now(),
      },
      type: 'PAGE_IS_LOADED',
    })
  }
}

export interface RootState {
  app: AppState
  asyncState: AsyncState<AllActions>
  user: bayes.bob.User
}

export type GetLocalStatsAction = AsyncAction<'GET_LOCAL_STATS', bayes.bob.LaborStatsData> &
ProjectAction<'GET_LOCAL_STATS'>

function getLaborStats(romeId: string, departementId?: string):
ThunkAction<Promise<bayes.bob.LaborStatsData|void>, unknown, unknown, GetLocalStatsAction>
function getLaborStats(project: bayes.bob.Project):
ThunkAction<Promise<bayes.bob.LaborStatsData|void>, unknown, unknown, GetLocalStatsAction>
function getLaborStats(projectOrRomeId: bayes.bob.Project|string, departementId = ''):
ThunkAction<Promise<bayes.bob.LaborStatsData|void>, unknown, unknown, GetLocalStatsAction> {
  const project: bayes.bob.Project = typeof projectOrRomeId === 'string' ?
    {city: {departementId}, targetJob: {jobGroup: {romeId: projectOrRomeId}}} :
    projectOrRomeId
  const user = {projects: [project]}
  const {
    city: {departementId: dptId = ''} = {},
    targetJob: {jobGroup: {romeId = ''} = {}} = {},
  } = project
  return wrapAsyncAction(
    'GET_LOCAL_STATS', (): Promise<bayes.bob.LaborStatsData> => projectLaborStatsPost(user),
    // TODO(cyrille): Only keep the project departementId and romeId.
    {project},
    `GET_LOCAL_STATS:${romeId}:${dptId}`,
  )
}


// Type of the main dispatch function.
export type DispatchAllActions =
  // Add actions as required.
  ThunkDispatch<RootState, unknown, PageIsLoadedAction> &
  Dispatch<AllActions>

export type AllActions =
  | DisplayToastMessageAction
  | GetLocalStatsAction
  | HideToasterMessageAction
  | PageIsLoadedAction


export const noOp = (): void => {
  // Do nothing.
}

export const useDispatch: () => DispatchAllActions = reduxUseDispatch

export {
  displayToasterMessage,
  getLaborStats,
  hideToasterMessageAction,
  pageIsLoaded,
}
